import { createPortal } from "react-dom";
import {
  X,
  Camera,
  Upload,
  Check,
  Download,
  RefreshCw,
  Cloud,
  Folder,
  Zap,
  Shield,
  ArrowLeft,
} from "lucide-react";
import useVirtualTryOn from "../hooks/useVirtualTryOn";

/**
 * @param {object}  props
 * @param {boolean} props.open
 * @param {string}  props.article_id
 * @param {string|null} [props.autoStart] - Launch straight into this mode
 *   ("custom_model" | "default" | null) instead of showing the picker. The
 *   agent sets it so a spoken "how does this look on me?" renders without
 *   stopping to ask which model to use.
 */
const VirtualTryOn = ({
  open,
  onClose,
  productImage,
  article_id,
  autoStart = null,
}) => {
  // All state and behaviour live in the shared hook (also used by the inline
  // mirror on product pages), so this popup and the mirror cannot drift.
  const vto = useVirtualTryOn({
    open,
    articleId: article_id,
    autoStart,
    onError: (message) => alert(message),
  });
  const {
    step,
    stepLabel,
    personImagePreview,
    resultImage,
    imageError,
    showUploadArea,
    setShowUploadArea,
    fileInputRef,
    handleFileSelect,
    retryImage,
    clearPhoto,
  } = vto;
  const handleTryOn = vto.start;
  const handleDownload = vto.download;
  const handleTryAnother = vto.tryAnother;
  const handleBackToUpload = vto.backToUpload;
  const handleResultImageLoaded = vto.onResultLoaded;
  const handleResultImageError = vto.onResultError;

  const handleClose = () => {
    vto.reset();
    onClose();
  };

  if (!open) return null;


  // Portalled to <body>: this overlay is rendered from inside product pages
  // and the avatar widget, and any transform / filter on an ancestor would
  // become the containing block for a fixed child and pin it to that box.
  return createPortal(
    <div
      className="ob-modalwrap"
      role="dialog"
      aria-modal="true"
      aria-label="Virtual try-on"
    >
      <div className="ob-scrim ob-show" onClick={handleClose} aria-hidden="true" />

      <div
        className="ob-modal relative w-full max-w-[940px] max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="ob-vtohead">
          <span
            className="ob-blob"
            style={{
              width: 200,
              height: 200,
              top: -120,
              right: 40,
              background: "rgba(255,255,255,.7)",
            }}
          />

          <div className="relative flex items-center gap-3">
            {step === 3 && (
              <button
                type="button"
                onClick={handleBackToUpload}
                className="ob-vtoiconbtn"
                aria-label="Back"
                title="Back"
              >
                <ArrowLeft size={17} />
              </button>
            )}

            <span className="ob-vtoglyph" aria-hidden="true">
              <Camera size={18} />
            </span>

            <div className="flex-1 min-w-0">
              <div
                className="text-[9.5px] font-bold uppercase tracking-[.16em]"
                style={{ color: "var(--ob-ink-2)" }}
              >
                {stepLabel}
              </div>
              <div className="ob-display text-[21px] leading-tight mt-0.5" style={{ color: "var(--ob-ink)" }}>
                Virtual try-on
              </div>
            </div>

            <button
              type="button"
              onClick={handleClose}
              className="ob-vtoiconbtn"
              aria-label="Close"
              title="Close"
            >
              <X size={17} />
            </button>
          </div>

          {/* Progress rail */}
          <div className="relative flex items-center gap-2.5 mt-4">
            {["Choose", "Render", "Result"].map((name, i) => {
              const idx = i + 1;
              const done = step > idx;
              const active = step === idx;
              return (
                <div key={name} className="flex items-center gap-2.5 flex-1 last:flex-none">
                  <span
                    className="ob-vtostep"
                    data-state={done ? "done" : active ? "active" : "todo"}
                  >
                    {done ? <Check size={12} /> : idx}
                  </span>
                  <span
                    className="text-[11px] font-semibold whitespace-nowrap"
                    style={{
                      color: done || active ? "var(--ob-ink)" : "var(--ob-ink-2)",
                    }}
                  >
                    {name}
                  </span>
                  {idx < 3 && (
                    <span className="ob-vtorail" data-filled={step > idx} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 min-h-0 overflow-y-auto ob-scroll p-5">
          {step === 1 && (
            <>
              {showUploadArea ? (
                <div className="ob-fadein">
                  <button
                    type="button"
                    onClick={() => {
                      setShowUploadArea(false);
                      clearPhoto();
                    }}
                    className="ob-btn ob-btn-quiet ob-btn-sm ob-ink2 mb-3"
                  >
                    <ArrowLeft size={14} />
                    Back to options
                  </button>

                  <div
                    className={`ob-dropzone ${personImagePreview ? "ob-has-file" : ""}`}
                  >
                    {personImagePreview ? (
                      <>
                        <img
                          src={personImagePreview}
                          alt="Preview"
                          className="ob-dropzone-preview"
                        />
                        <button
                          type="button"
                          className="ob-btn ob-btn-ghost ob-btn-sm mt-4"
                          onClick={() => {
                            clearPhoto();
                            fileInputRef.current?.click();
                          }}
                        >
                          Change photo
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="ob-dropzone-glyph">
                          <Upload size={22} />
                        </span>
                        <div className="text-[15px] font-semibold ob-ink mt-3">
                          Upload your photo
                        </div>
                        <p className="text-[12.5px] ob-ink2 mt-1.5 max-w-[320px] mx-auto leading-relaxed">
                          A clear, full-body photo against a plain background
                          gives the best result.
                        </p>
                        <button
                          type="button"
                          className="ob-btn ob-btn-solid mt-4"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <Cloud size={15} />
                          Choose a photo
                        </button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          style={{ display: "none" }}
                          onChange={handleFileSelect}
                        />
                      </>
                    )}
                  </div>

                  <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
                    <span className="ob-chip">
                      <Folder size={12} /> JPG, PNG
                    </span>
                    <span className="ob-chip">
                      <Zap size={12} /> Max 10MB
                    </span>
                    <span className="ob-chip ob-chip-ok">
                      <Shield size={12} /> 100% private
                    </span>
                  </div>
                </div>
              ) : (
                <div className="grid md:grid-cols-[1fr,300px] gap-5 items-start ob-fadein">
                  {/* Options */}
                  <div className="ob-stagger space-y-2.5">
                    <button
                      type="button"
                      onClick={() => handleTryOn("custom_model")}
                      className="ob-vtooption ob-vtooption-primary"
                    >
                      <span className="ob-vtooption-glyph">
                        <Zap size={17} />
                      </span>
                      <span className="flex-1 text-left">
                        <span className="ob-vtooption-title">
                          Use my profile picture
                        </span>
                        <span className="ob-vtooption-sub">
                          Fastest — nothing to upload. Recommended.
                        </span>
                      </span>
                      <span className="ob-vtooption-arrow">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="w-[15px] h-[15px]"
                        >
                          <path d="m9 18 6-6-6-6" />
                        </svg>
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setShowUploadArea(true)}
                      className="ob-vtooption"
                    >
                      <span className="ob-vtooption-glyph">
                        <Upload size={17} />
                      </span>
                      <span className="flex-1 text-left">
                        <span className="ob-vtooption-title">
                          Upload a photo
                        </span>
                        <span className="ob-vtooption-sub">
                          Your own full-body shot for the truest fit.
                        </span>
                      </span>
                      <span className="ob-vtooption-arrow">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="w-[15px] h-[15px]"
                        >
                          <path d="m9 18 6-6-6-6" />
                        </svg>
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleTryOn("default")}
                      className="ob-vtooption"
                    >
                      <span className="ob-vtooption-glyph">
                        <Camera size={17} />
                      </span>
                      <span className="flex-1 text-left">
                        <span className="ob-vtooption-title">
                          Use a default model
                        </span>
                        <span className="ob-vtooption-sub">
                          Just want to see the garment on a body.
                        </span>
                      </span>
                      <span className="ob-vtooption-arrow">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="w-[15px] h-[15px]"
                        >
                          <path d="m9 18 6-6-6-6" />
                        </svg>
                      </span>
                    </button>

                    <div className="flex items-center gap-2 pt-2 flex-wrap">
                      <span className="ob-chip">
                        <Folder size={12} /> JPG, PNG
                      </span>
                      <span className="ob-chip">
                        <Zap size={12} /> Max 10MB
                      </span>
                      <span className="ob-chip ob-chip-ok">
                        <Shield size={12} /> 100% private
                      </span>
                    </div>
                  </div>

                  {/* The garment being tried on — context for the choice. */}
                  <div className="ob-mirror ob-mirror-still hidden md:block">
                    {productImage ? (
                      <img
                        src={productImage}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div
                        className="text-center px-5"
                        style={{ color: "var(--ob-ink-2)" }}
                      >
                        <div className="text-[12.5px] font-semibold" style={{ color: "var(--ob-ink)" }}>
                          Mirror ready
                        </div>
                        <div className="text-[11px] mt-1.5 leading-relaxed">
                          Pick how you want to see it on.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {step === 2 && (
            <div className="py-10 text-center ob-fadein">
              {/* The mirror sheen is the wait indicator — it says "something
                  is being made", which a bare spinner does not. */}
              <div className="ob-mirror max-w-[280px] mx-auto">
                <div className="text-center px-6 relative">
                  <div className="ob-vtospinner" aria-hidden="true" />
                  <div className="text-[13px] font-semibold mt-4" style={{ color: "var(--ob-ink)" }}>
                    Rendering your try-on
                  </div>
                  <div
                    className="text-[11.5px] mt-1.5 leading-relaxed"
                    style={{ color: "var(--ob-ink-2)" }}
                  >
                    This takes a few moments.
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-center gap-1.5 mt-5 ob-ink3">
                <span className="ob-tdot" />
                <span className="ob-tdot" />
                <span className="ob-tdot" />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="ob-fadein">
              <div className="ob-vtoresult">
                {resultImage ? (
                  imageError ? (
                    <div className="text-center p-6 max-w-[420px] mx-auto">
                      <div className="ob-empty-glyph" style={{ color: "var(--ob-err)" }}>
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={1.7}
                          strokeLinecap="round"
                          className="w-6 h-6"
                        >
                          <circle cx="12" cy="12" r="9" />
                          <path d="M12 8v5M12 16.2v.1" />
                        </svg>
                      </div>
                      <div className="text-[14px] font-semibold ob-ink">
                        We couldn&rsquo;t load the image
                      </div>
                      <p className="text-[12px] ob-ink2 mt-2 leading-relaxed">
                        It may be a permissions problem, a network hiccup, or an
                        expired link.
                      </p>
                      <button
                        type="button"
                        className="ob-btn ob-btn-ghost ob-btn-sm mt-4"
                        onClick={retryImage}
                      >
                        <RefreshCw size={14} />
                        Try loading again
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="ob-vtobadge">Virtual try-on</span>
                      <img
                        src={resultImage}
                        alt="Try-on result"
                        referrerPolicy="no-referrer"
                        crossOrigin="anonymous"
                        onError={(e) => {
                          console.error("Image error event:", e);
                          handleResultImageError();
                        }}
                        onLoad={handleResultImageLoaded}
                        className="ob-vtoresult-img"
                      />
                    </>
                  )
                ) : (
                  <div className="ob-ink3 text-[12.5px] py-16">
                    No result available
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-2 mt-4">
                <button
                  type="button"
                  onClick={handleTryAnother}
                  className="ob-btn ob-btn-solid ob-btn-lg flex-1"
                >
                  <RefreshCw size={15} />
                  Try another
                </button>
                <button
                  type="button"
                  onClick={handleDownload}
                  disabled={!resultImage}
                  className="ob-btn ob-btn-ghost ob-btn-lg flex-1"
                >
                  <Download size={15} />
                  Download
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default VirtualTryOn;
